import stringify from 'json-stable-stringify';

/**
 * A custom map class, allowing more key types than the builtin ES6 Map and plain JS objects.
 *
 * Map is immutable, meaning methods like set and delete do not mutate the base object
 * but rather return clones of the base object with the relevant key-value pair added/removed/updated.
 *
 * Allows keys of any type that are convertible to strings using json-stable-stringify.
 * Keys should be uniquely representable with their JSON representations. Classes that wish to be used
 * as keys in ImmutableMap can implement toJSON.
 * Keys should not be mutated after adding to the map.
 * Keys with the same stingify output are considered equal.
 *
 *
 * Example:
 *
 * interface Point {
 *  x: number;
 *  y: number
 * }
 *
 * const pointToLength = new ImmutableMap<Point, number>().set({x: 3, y: 4}, 5);
 * pointToLength.has({x: 3, y: 4});    // outputs true
 * pointToLength.delete({y: 4, x: 3}); // outputs ImmutableMap with no entries
 * pointToLength.set({x: 3}, 5);       // does not typecheck (missing y from interface)
 *
 * Also note that an object satisfying Point but with extra fields will be treated as a unique key.
 * Discussion: https://github.com/benchling/aurelia/pull/13636#issuecomment-649791776
 *
 * Other useful examples can be found in './__tests__/ImmutableMap-test.ts'
 */
export default class ImmutableMap<K, V> {
  private map: Map<string, {key: K; value: V}>;

  constructor(items?: Array<[K, V]>) {
    this.map = new Map((items ?? []).map(([key, value]) => [this.keyToString(key), {key, value}]));
  }

  private keyToString(key: K): string {
    return stringify(key);
  }

  get(key: K): V | null {
    return this.map.get(this.keyToString(key))?.value ?? null;
  }

  has(key: K): boolean {
    return this.map.has(this.keyToString(key));
  }

  size(): number {
    return this.map.size;
  }

  set(key: K, value: V): ImmutableMap<K, V> {
    const newMap = new ImmutableMap<K, V>();
    newMap.map = new Map(this.map);
    newMap.map.set(this.keyToString(key), {key, value});
    return newMap;
  }

  transformValue(key: K, valueTransform: (value: V) => V): ImmutableMap<K, V> {
    const keyString = this.keyToString(key);

    const newMap = new ImmutableMap<K, V>();
    newMap.map = new Map(this.map);
    const kvPair = newMap.map.get(keyString);
    if (kvPair === undefined) {
      throw new Error(`Key ${this.keyToString(key)} not found in ImmutableMap`);
    }
    newMap.map.set(keyString, {key, value: valueTransform(kvPair.value)});
    return newMap;
  }

  transformValues(valueTransform: (value: V) => V): ImmutableMap<K, V> {
    const newMap = new ImmutableMap<K, V>();
    for (const [key, value] of this) {
      newMap.map.set(this.keyToString(key), {key, value: valueTransform(value)});
    }
    return newMap;
  }

  delete(key: K): ImmutableMap<K, V> {
    const newMap = new ImmutableMap<K, V>();
    newMap.map = new Map(this.map);
    newMap.map.delete(this.keyToString(key));
    return newMap;
  }

  *keys(): IterableIterator<K> {
    for (const {key} of this.map.values()) {
      yield key;
    }
  }

  *values(): IterableIterator<V> {
    for (const {value} of this.map.values()) {
      yield value;
    }
  }

  *[Symbol.iterator](): IterableIterator<[K, V]> {
    for (const {key, value} of this.map.values()) {
      yield [key, value];
    }
  }
}
